import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const VerifyEmail = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setError('No verification token provided');
      return;
    }
    
    const verifyToken = async () => {
      try {
        // ── Commandment VI: email verification is a POST (state-mutating action) ──
        const response = await axios.post(`${API}/api/auth/verify-email`, { token });
        setSuccess(response.data.verified);
      } catch (err) {
        setError(err.response?.data?.detail || 'Verification failed');
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-600 mb-4" />
            <p className="text-gray-600">{t('verify_email.verifying')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-2xl text-emerald-700">{t('verify_email.success_title')}</CardTitle>
            <CardDescription className="text-base">
              {t('verify_email.success_desc')}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700">
              <Link to="/login">{t('reset_password.go_to_login')}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <CardTitle className="text-xl text-red-700">{t('verify_email.error_title')}</CardTitle>
          <CardDescription className="text-base">
            {error || t('verify_email.error_desc')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-50 p-4 rounded-lg text-sm text-gray-600">
            <p className="font-medium text-gray-900 mb-2">{t('verify_email.request_new')}</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Request a new verification email from the login page</li>
              <li>Check if you clicked the most recent verification link</li>
              <li>Contact support if the problem persists</li>
            </ul>
          </div>
          
          <div className="flex flex-col gap-2">
            <Button asChild className="w-full bg-emerald-600 hover:bg-emerald-700">
              <Link to="/login">{t('verify_email.back_to_login')}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
